// Mock notifications for the member-app Notification Centre.
//
// Shape note for promotion: the real model (lib/models notification) is
// { id, title, body, deepLink?, readAt: string | null, createdAt }. Here we use
// a display `when` string and a `read` boolean (prototype-friendly), and a
// `group` for the date sections (the real screen derives it from createdAt).
//
// `type` no longer draws anything. The rows used to carry a tinted icon circle
// per type and it came off, so this is data we hold rather than something the
// screen reads.

export type NotificationType =
  'call' | 'action' | 'document' | 'reminder' | 'shortlist';

export type NotificationGroup = 'today' | 'week' | 'earlier';

export type NotificationItem = {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  when: string;
  read: boolean;
  group: NotificationGroup;
  deepLink?: string;
};

// The publish notification, exported on its own because two surfaces say it: this
// feed, and the push sketch in the paywall handover. One copy, so a change to the
// words cannot land on one and miss the other.
//
// It says work is underway rather than announcing a shortlist. "Your shortlist is
// ready" on a list with nobody on it is the version we ruled out; naming the wait
// is what makes a notification at publish worth sending at all.
export const SHORTLIST_STARTED = {
  title: 'Your care expert is working on your shortlist',
  body: 'We’ve asked care providers near you about availability. We’ll let you know as soon as one confirms.',
} as const;

export const NOTIFICATIONS: NotificationItem[] = [
  // The three Project MC notifications, in the order a carer meets them. Copy is
  // a first draft and the point of having them here is to work on it.
  //
  // Nothing fires when the shortlist publishes. Under the paywall the list goes
  // live before any provider has paid, so "your shortlist is ready" would land on
  // an empty screen — which is why the row that used to say that is now the first
  // arrival instead.
  {
    id: 'n-shortlist-started',
    type: 'shortlist',
    title: SHORTLIST_STARTED.title,
    body: SHORTLIST_STARTED.body,
    when: '4h',
    read: false,
    group: 'today',
    deepLink: '/karehero-app/shortlists-v1',
  },
  {
    id: 'n-first-provider',
    type: 'shortlist',
    title: 'A new provider on your shortlist',
    body: 'Riverside lodge can help with Mum’s care. Have a look when you’re ready.',
    when: '1h',
    read: false,
    group: 'today',
    deepLink: '/karehero-app/shortlists-v1',
  },
  // The second and every one after it, grouped. Five providers answering across an
  // afternoon is not five pushes.
  {
    id: 'n-more-providers',
    type: 'shortlist',
    title: '2 more providers on your shortlist',
    body: 'Oakwood care home and Cherry Tree house can help too.',
    when: '3h',
    read: false,
    group: 'today',
    deepLink: '/karehero-app/shortlists-v1',
  },
  // Three days, nobody. The carer hears something rather than nothing, and what it
  // should say depends on what we actually do at that point — still open.
  {
    id: 'n-still-waiting',
    type: 'shortlist',
    title: 'Still waiting to hear back',
    body: 'Your care expert is chasing providers for Mum’s care. We’ll let you know as soon as one confirms.',
    when: '3d',
    read: true,
    group: 'week',
    deepLink: '/karehero-app/shortlists-v1',
  },
  {
    id: 'n1',
    type: 'action',
    title: 'Action due tomorrow',
    body: "Upload Mum's financial statements",
    when: '2h',
    read: false,
    group: 'today',
    deepLink: '/karehero-app/actions',
  },
  {
    id: 'n2',
    type: 'call',
    title: 'Call summary ready',
    body: 'Notes from your care assessment with Yasmin',
    when: '5h',
    read: false,
    group: 'today',
    deepLink: '/karehero-app/profile',
  },
  {
    id: 'n3',
    type: 'document',
    title: 'Document requested',
    body: "Yasmin asked for Mum's care needs assessment",
    when: 'Tue',
    read: true,
    group: 'week',
    deepLink: '/karehero-app/profile',
  },
  {
    id: 'n4',
    type: 'action',
    title: 'Action completed',
    body: 'Power of attorney form submitted',
    when: 'Mon',
    read: true,
    group: 'week',
    deepLink: '/karehero-app/actions',
  },
  {
    id: 'n5',
    type: 'call',
    title: 'Call booked',
    body: 'Funding call confirmed for 10th June at 10:00',
    when: '2 weeks ago',
    read: true,
    group: 'earlier',
    deepLink: '/karehero-app/profile',
  },
  {
    id: 'n6',
    type: 'reminder',
    title: 'Welcome to KareHero',
    body: "Here's how to get started with your care plan",
    when: '3 weeks ago',
    read: true,
    group: 'earlier',
    deepLink: '/karehero-app/home',
  },
];
